import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
  Lock as LockIcon,
  PhoneAndroid as PhoneAndroidIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { loginStart, loginSuccess, loginFailure } from '../../store/slices/authSlice';
import { authService } from '../../services/authService';
import LoadingOverlay from '../../components/Shared/LoadingOverlay';
import OTPLogin from './OTPLogin';

import sikkimSeal from '../../static/assets/imgs/seal_of_sikkim_256px.png';

const validationSchema = yup.object({
  username: yup
    .string()
    .required('Username is required')
    .min(3, 'Username must be at least 3 characters'),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
});

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, error } = useSelector((state) => state.auth);
  
  const [showPassword, setShowPassword] = useState(false);
  const [loginTab, setLoginTab] = useState(0); // 0: Username/Password, 1: OTP

  const from = location.state?.from?.pathname || '/dashboard';

  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        dispatch(loginStart());
        
        const response = await authService.login(values);
        
        dispatch(loginSuccess({
          username: response.username,
          full_name: response.full_name,
          access_token: response.access_token,
          // refresh_token is in httpOnly cookie
          role: response.role,
          id: response.id,
          district: response.district,
          subdivision: response.subdivision,
          block: response.block,
        }));
        
        toast.success('Login successful!');
        navigate(from, { replace: true });
      } catch (error) {
        const message = error.response?.data?.message || 'Login failed';
        dispatch(loginFailure(message));
        toast.error(message);
      }
    },
  });

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: '100vh',
          justifyContent: 'center',
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 400, boxShadow: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <img
                src={sikkimSeal}
                alt="Government of Sikkim"
                style={{ width: '140px', marginBottom: 16 }}
              />

              <Typography component="h1" variant="h4" fontWeight="bold" gutterBottom>
                NC ExMS
              </Typography>
              <Typography variant="h6" color="text.secondary">
                ExGratia Management System
              </Typography>

            </Box>

            {/* Login Tab Selection */}
            <Tabs
              value={loginTab}
              onChange={(e, newValue) => setLoginTab(newValue)}
              variant="fullWidth"
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab
                label="Sign In"
                icon={<PersonIcon />}
                iconPosition="start"
                sx={{ textTransform: 'none' }}
              />
              <Tab
                label="OTP Login"
                icon={<PhoneAndroidIcon />}
                iconPosition="start"
                sx={{ textTransform: 'none' }}
              />
            </Tabs>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Tab 0: Username/Password Login */}
            {loginTab === 0 && (
            <Box component="form" onSubmit={formik.handleSubmit}>
              <TextField
                fullWidth
                id="username"
                name="username"
                label="Username"
                value={formik.values.username}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.username && Boolean(formik.errors.username)}
                helperText={formik.touched.username && formik.errors.username}
                margin="normal"
                InputProps={{
                  spellCheck: false,
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                disabled={isLoading}
              />

              <TextField
                fullWidth
                id="password"
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                disabled={isLoading}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={isLoading || !formik.isValid}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Sign In'
                )}
              </Button>
            </Box>
            )}

            {/* Tab 1: OTP Login */}
            {loginTab === 1 && <OTPLogin />}
          </CardContent>
        </Card>

        <Typography variant="h6" color="text.secondary" align="center" sx={{ mt: 4, mb: 1 }}>
          © {new Date().getFullYear()} Government of Sikkim. All rights reserved.
        </Typography>
        <Typography variant='body2' color="text.secondary" align="center">
            Developed by IT Section, District Administrative Centre, Mangan
        </Typography>

      </Box>

      {/* Loading Overlay */}
      <LoadingOverlay
        open={isLoading}
        message="Signing in..."
      />
    </Container>
  );
};

export default Login;
